import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { User, Briefcase, Users, Copy, Trash2, UserPlus, Globe2, Check, Image as ImageIcon, Upload, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useToast } from '../hooks/useToast';
import { useTenantFormat } from '../hooks/useTenantFormat';
import { api } from '../lib/api';
import { GlassCard } from '../components/GlassCard';
import Drawer from '../components/Drawer';
import ConfirmModal from '../components/ConfirmModal';

const INPUT = 'w-full bg-white/[0.06] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-white/30 outline-none focus:border-[#7C4DFF]/50 focus:ring-1 focus:ring-[#7C4DFF]/25 transition-colors';
const TABS = [
  { key: 'profile', label: 'My Profile', Icon: User },
  { key: 'business', label: 'Business', Icon: Briefcase },
  { key: 'team', label: 'Team', Icon: Users },
  { key: 'brand', label: 'Brand Info', Icon: ImageIcon },
];
const ROLES = ['manager', 'accountant', 'sales', 'warehouse'];

export default function Profile() {
  const [tab, setTab] = useState('profile');

  return (
    <div className="p-6 lg:p-8 max-w-[1200px] mx-auto" data-testid="profile-page">
      <h1 className="text-2xl font-extrabold text-white tracking-tight mb-6" data-testid="profile-title">
        Profile
      </h1>
      <div className="flex gap-1 p-1 rounded-2xl bg-white/[0.04] border border-white/10 mb-8 w-fit" data-testid="profile-tabs">
        {TABS.map(t => (
          <button key={t.key} data-testid={`profile-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${tab === t.key ? 'bg-white/10 text-white shadow-sm' : 'text-white/50 hover:text-white/70'}`}>
            <t.Icon size={16} />
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'profile' && <MyProfileTab />}
      {tab === 'business' && <BusinessTab />}
      {tab === 'team' && <TeamTab />}
      {tab === 'brand' && <BrandInfoTab />}
    </div>
  );
}

function MyProfileTab() {
  const { user, tenant, token } = useAuth();
  const toast = useToast();
  const initials = user?.name?.split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase() || '?';
  const [showPw, setShowPw] = useState(false);
  const [pw, setPw] = useState({ current: '', new_password: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);

  const changePw = async () => {
    if (!pw.current || !pw.new_password) { toast.show('Fill all fields', 'error'); return; }
    if (pw.new_password.length < 6) { toast.show('Minimum 6 characters', 'error'); return; }
    if (pw.new_password !== pw.confirm) { toast.show('Passwords don\'t match', 'error'); return; }
    setPwSaving(true);
    try {
      await api.changePassword(token, { current_password: pw.current, new_password: pw.new_password });
      toast.show('Password changed', 'success');
      setPw({ current: '', new_password: '', confirm: '' });
      setShowPw(false);
    } catch (e) { toast.show(e?.message || 'Failed', 'error'); }
    finally { setPwSaving(false); }
  };

  return (
    <div className="max-w-md space-y-6" data-testid="my-profile-tab">
      <div className="glass-card p-8 flex flex-col items-center text-center">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#7C4DFF] to-teal-500 flex items-center justify-center shadow-lg mb-4">
          <span className="text-white text-2xl font-bold">{initials}</span>
        </div>
        <p className="text-xl font-bold text-white" data-testid="profile-user-name">{user?.name}</p>
        <p className="text-white/50 text-sm mt-1" data-testid="profile-user-email">{user?.email}</p>
        <span className="mt-3 inline-block px-3 py-1 rounded-full bg-teal-500/15 border border-teal-500/25 text-teal-400 text-[11px] font-bold uppercase tracking-wider" data-testid="profile-user-role">
          {user?.role}
        </span>
        {tenant?.name && <p className="text-white/40 text-sm mt-4" data-testid="profile-tenant-name">{tenant.name}</p>}
      </div>
      <div className="glass-card p-6">
        <button data-testid="toggle-change-password" onClick={() => setShowPw(v => !v)}
          className="text-sm text-teal-400 font-semibold hover:text-teal-300 transition-colors">
          {showPw ? 'Cancel' : 'Change Password'}
        </button>
        {showPw && (
          <div className="mt-4 space-y-3 animate-fade-in">
            <input data-testid="pw-current" type="password" className={INPUT} placeholder="Current password" value={pw.current} onChange={e => setPw(p => ({ ...p, current: e.target.value }))} />
            <input data-testid="pw-new" type="password" className={INPUT} placeholder="New password" value={pw.new_password} onChange={e => setPw(p => ({ ...p, new_password: e.target.value }))} />
            <input data-testid="pw-confirm" type="password" className={INPUT} placeholder="Confirm new password" value={pw.confirm} onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))} />
            <button data-testid="pw-save-btn" onClick={changePw} disabled={pwSaving}
              className="w-full h-11 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-teal-500/25 transition-all disabled:opacity-50">
              {pwSaving ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin mx-auto" /> : 'Update Password'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function BusinessTab() {
  const { token, user } = useAuth();
  const { refreshRegion, profile } = useTenant();
  const toast = useToast();
  const { taxIdLabel } = useTenantFormat();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Region & Tax state
  const [presets, setPresets] = useState([]);
  const [regionSaving, setRegionSaving] = useState(false);
  const [regionForm, setRegionForm] = useState({
    country_code: 'IN', currency_symbol: '₹', tax_rate_default: '18',
  });

  useEffect(() => {
    api.regionsPresets()
      .then(r => setPresets(r?.items ?? []))
      .catch(() => setPresets([]));
  }, []);

  useEffect(() => {
    if (!token) return;
    api.tenantMe(token)
      .then(r => {
        const t = r.tenant || r;
        setForm({
          name: t.name || '', address_line1: t.address_line1 || '', city: t.city || '',
          state: t.state || '', pincode: t.pincode || '', gstin: t.gstin || '',
          phone: t.phone || '', email: t.email || '',
          bank_name: t.bank_name || '', bank_account_holder: t.bank_account_holder || '',
          bank_account_number: t.bank_account_number || '', bank_ifsc: t.bank_ifsc || '',
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // Load current region
    api.regionGet(token)
      .then(r => {
        setRegionForm({
          country_code: r?.country_code || 'IN',
          currency_symbol: r?.currency_symbol || '₹',
          tax_rate_default: String(r?.tax_rate_default ?? 18),
        });
      })
      .catch(() => {});
  }, [token]);

  const pickCountry = (code) => {
    const preset = presets.find(p => p.country_code === code);
    if (!preset) { setRegionForm(f => ({ ...f, country_code: code })); return; }
    setRegionForm({
      country_code: code,
      currency_symbol: preset.currency_symbol,
      tax_rate_default: String(preset.tax_rate_default),
    });
  };

  const saveRegion = async () => {
    if (!token) return;
    const rate = Number(regionForm.tax_rate_default);
    if (!Number.isFinite(rate) || rate < 0 || rate > 30) {
      toast.show('Tax rate must be between 0 and 30', 'error'); return;
    }
    setRegionSaving(true);
    try {
      await api.regionPatch(token, {
        country_code: regionForm.country_code,
        currency_symbol: regionForm.currency_symbol,
        tax_rate_default: rate,
      });
      await refreshRegion();
      toast.show('Region updated', 'success');
    } catch (e) {
      toast.show(e?.message ?? 'Region save failed', 'error');
    } finally { setRegionSaving(false); }
  };

  const save = async () => {
    if (!token) return;
    setSaving(true);
    try {
      await api.tenantMePatch(token, form);
      toast.show('Business info saved', 'success');
    } catch (e) {
      toast.show(e?.message || 'Save failed', 'error');
    } finally { setSaving(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const activePreset = presets.find(p => p.country_code === regionForm.country_code);
  const activeTaxLabel = regionForm.country_code === 'IN' ? 'GST' : 'VAT';

  return (
    <div className="max-w-2xl space-y-6" data-testid="business-tab">
      {/* Region & Tax card */}
      <div className="glass-card p-6 space-y-4" data-testid="ws-region-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-white">Region & Tax</h3>
            <p className="text-white/50 text-sm mt-0.5">
              {activePreset?.flag ?? ''} {activePreset?.country_name ?? regionForm.country_code}
              {'  ·  '}{regionForm.currency_symbol} {activePreset?.currency_code ?? ''}
              {'  ·  '}{activeTaxLabel} {regionForm.tax_rate_default}%
            </p>
          </div>
          <Globe2 size={22} className="text-[#7C4DFF]" />
        </div>

        <div>
          <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">Country</span>
          <div className="mt-2 space-y-1.5" data-testid="ws-country-grid">
            {presets.map(p => {
              const on = p.country_code === regionForm.country_code;
              return (
                <button
                  key={p.country_code}
                  onClick={() => pickCountry(p.country_code)}
                  data-testid={`ws-country-${p.country_code}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all text-left ${
                    on ? 'bg-[#7C4DFF] border-[#7C4DFF] text-white' : 'bg-white/[0.04] border-white/10 text-white/80 hover:border-white/20'
                  }`}
                >
                  <span className="text-xl">{p.flag}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-bold ${on ? 'text-white' : 'text-white/90'}`}>{p.country_name}</p>
                    <p className={`text-xs ${on ? 'text-white/75' : 'text-white/40'}`}>{p.currency_symbol} {p.currency_code} · {p.tax_label} {p.tax_rate_default}%</p>
                  </div>
                  {on && <Check size={16} className="text-white flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>

        <label className="block" htmlFor="ws-currency">
          <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">Currency symbol</span>
          <input
            id="ws-currency"
            data-testid="ws-currency-symbol"
            className={`mt-1.5 ${INPUT}`}
            value={regionForm.currency_symbol}
            onChange={e => setRegionForm(f => ({ ...f, currency_symbol: e.target.value }))}
            maxLength={6}
          />
        </label>

        <label className="block">
          <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">Tax label (auto)</span>
          <div className="mt-1.5 w-full bg-[#7C4DFF]/10 border border-[#7C4DFF]/20 rounded-xl px-4 py-3 text-[#7C4DFF] font-bold text-sm">
            {activeTaxLabel}
          </div>
        </label>

        <label className="block" htmlFor="ws-tax-rate">
          <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">Default tax rate (%) · 0–30</span>
          <input
            id="ws-tax-rate"
            data-testid="ws-tax-rate"
            className={`mt-1.5 ${INPUT}`}
            value={regionForm.tax_rate_default}
            onChange={e => setRegionForm(f => ({ ...f, tax_rate_default: e.target.value.replace(/[^0-9.]/g, '') }))}
            maxLength={5}
          />
        </label>

        <button
          data-testid="ws-region-save"
          onClick={saveRegion}
          disabled={regionSaving}
          className="w-full h-11 rounded-xl bg-gradient-to-r from-[#7C4DFF] to-[#5B2ED8] text-white font-bold text-sm hover:shadow-lg hover:shadow-[#7C4DFF]/25 transition-all disabled:opacity-50"
        >
          {regionSaving ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin mx-auto" /> : 'Save Region'}
        </button>
      </div>

      {/* Phase 23 Step 3 — Industry drives the School & Club module. */}
      <div className="glass-card p-6" data-testid="biz-industry-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">Industry</span>
            <p className="text-white font-semibold mt-1" data-testid="biz-industry-value">{profile?.industry_display || 'Not set'}</p>
          </div>
          {user?.role === 'owner' && (
            <Link to="/onboarding" data-testid="biz-change-industry"
              className="px-4 py-2 rounded-xl bg-white/[0.06] border border-white/10 text-teal-400 text-sm font-semibold hover:bg-white/[0.1]">
              Change
            </Link>
          )}
        </div>
        <p className="text-white/40 text-xs mt-3">Selecting “School / Coaching” unlocks the School &amp; Club module (Students, Employees, Fee Plans, Billing). Requires Pro or Premium plan.</p>
      </div>

      <div className="glass-card p-6 space-y-4">
        <h3 className="text-base font-bold text-white">Business Info</h3>
        {[['name','Business Name'],['address_line1','Address'],['city','City'],['state','State'],['pincode','PIN Code'],['gstin', taxIdLabel],['phone','Phone'],['email','Email']].map(([k,l]) => (
          <label key={k} className="block" htmlFor={`biz-${k}`}>
            <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">{l}</span>
            <input id={`biz-${k}`} data-testid={`biz-field-${k}`} className={`mt-1.5 ${INPUT}`} value={form[k] || ''} onChange={e => set(k, e.target.value)} placeholder={l} />
          </label>
        ))}
      </div>
      <div className="glass-card p-6 space-y-4">
        <h3 className="text-base font-bold text-white">Bank Details (for invoices)</h3>
        {[['bank_name','Bank Name'],['bank_account_holder','Account Holder'],['bank_account_number','Account Number'],['bank_ifsc','IFSC']].map(([k,l]) => (
          <label key={k} className="block" htmlFor={`biz-${k}`}>
            <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">{l}</span>
            <input id={`biz-${k}`} data-testid={`biz-field-${k}`} className={`mt-1.5 ${INPUT}`} value={form[k] || ''} onChange={e => set(k, e.target.value)} placeholder={l} />
          </label>
        ))}
      </div>
      <button data-testid="biz-save-btn" onClick={save} disabled={saving}
        className="w-full h-12 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-teal-500/25 transition-all disabled:opacity-50">
        {saving ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin mx-auto" /> : 'Save Changes'}
      </button>
    </div>
  );
}

function TeamTab() {
  const { token, user } = useAuth();
  const toast = useToast();
  const canManage = user?.role === 'owner' || user?.role === 'manager';
  const isOwner = user?.role === 'owner';

  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRolePicker, setShowRolePicker] = useState(false);
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const m = await api.tenantUsers(token);
      setMembers(m.users || []);
      if (canManage) {
        const inv = await api.invitesList(token);
        setInvites((inv.items || []).filter(i => !i.used_by));
      }
    } catch (e) {
      toast.show(e?.message || 'Failed to load team', 'error');
    } finally { setLoading(false); }
  }, [token, canManage, toast]);

  useEffect(() => { load(); }, [load]);

  const createInvite = async (role) => {
    if (!token) return;
    setCreating(true);
    setShowRolePicker(false);
    try {
      const inv = await api.invitesCreate(token, role);
      setLastCreated(inv);
      load();
    } catch (e) {
      toast.show(e?.message || 'Failed to create invite', 'error');
    } finally { setCreating(false); }
  };

  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.show('Copied', 'success'); }
    catch { toast.show('Copy failed', 'error'); }
  };

  const revoke = async (id) => {
    if (!token) return;
    try { await api.invitesDelete(token, id); toast.show('Revoked', 'info'); load(); }
    catch (e) { toast.show(e?.message || 'Failed', 'error'); }
  };

  const removeMember = async () => {
    if (!token || !confirmRemove) return;
    setRemoving(true);
    try {
      await api.tenantRemoveUser(token, confirmRemove.id);
      toast.show(`${confirmRemove.name} removed`, 'success');
      setConfirmRemove(null);
      load();
    } catch (e) {
      toast.show(e?.message || 'Cannot remove', 'error');
    } finally { setRemoving(false); }
  };

  function timeAgo(iso) {
    if (!iso) return 'Never';
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6" data-testid="team-tab">
      <div>
        <h3 className="text-xs font-bold text-white/50 uppercase tracking-wider mb-3">Members ({members.length})</h3>
        <div className="glass-card overflow-hidden divide-y divide-white/[0.06]">
          {members.map(m => {
            const ini = (m.name || '?').split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase();
            return (
              <div key={m.id} data-testid={`member-${m.id}`} className="flex items-center gap-4 px-5 py-4">
                <div className="w-10 h-10 rounded-full bg-teal-500/15 flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-400 text-xs font-bold">{ini}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm">{m.name}</p>
                  <p className="text-white/40 text-xs mt-0.5">{m.email} · {timeAgo(m.last_login_at)}</p>
                </div>
                <span className="px-2.5 py-1 rounded-full bg-teal-500/10 text-teal-400 text-[10px] font-bold uppercase tracking-wider">{m.role}</span>
                {isOwner && m.id !== user?.id && (
                  <button data-testid={`remove-member-${m.id}`} onClick={() => setConfirmRemove(m)}
                    className="p-2 rounded-lg hover:bg-red-500/15 text-white/40 hover:text-red-400 transition-colors">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            );
          })}
          {members.length === 0 && (
            <div className="px-5 py-8 text-center"><p className="text-white/40 text-sm">No team members yet.</p></div>
          )}
        </div>
      </div>

      {canManage && (
        <div>
          <h3 className="text-xs font-bold text-white/50 uppercase tracking-wider mb-3">Pending Invites ({invites.length})</h3>
          {invites.length === 0 ? (
            <div className="glass-card p-5"><p className="text-white/40 text-sm">No active invites. Generate one below.</p></div>
          ) : (
            <div className="glass-card overflow-hidden divide-y divide-white/[0.06]">
              {invites.map(inv => (
                <div key={inv.id} data-testid={`invite-${inv.id}`} className="flex items-center gap-3 px-5 py-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-extrabold tracking-[0.15em] text-lg" data-testid={`invite-code-${inv.id}`}>{inv.code}</p>
                    <p className="text-white/40 text-xs mt-0.5">{inv.role?.toUpperCase()} · expires {inv.expires_at?.slice(0, 10)}</p>
                  </div>
                  <button data-testid={`copy-invite-${inv.id}`} onClick={() => copyCode(inv.code)}
                    className="p-2 rounded-lg bg-teal-500/10 hover:bg-teal-500/20 text-teal-400 transition-colors" title="Copy code">
                    <Copy size={16} />
                  </button>
                  <button data-testid={`revoke-invite-${inv.id}`} onClick={() => revoke(inv.id)}
                    className="p-2 rounded-lg hover:bg-red-500/15 text-white/40 hover:text-red-400 transition-colors" title="Revoke">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <button data-testid="generate-invite-btn" onClick={() => setShowRolePicker(true)} disabled={creating}
            className="mt-4 flex items-center gap-2 px-6 h-12 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-teal-500/25 transition-all disabled:opacity-50">
            {creating
              ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin" />
              : <><UserPlus size={18} /> Generate Invite Code</>
            }
          </button>
        </div>
      )}

      <Drawer open={showRolePicker} onClose={() => setShowRolePicker(false)} title="Pick role for invite">
        <div className="space-y-1">
          {ROLES.map(r => (
            <button key={r} data-testid={`pick-role-${r}`} onClick={() => createInvite(r)}
              className="w-full text-left px-5 py-4 rounded-xl hover:bg-white/[0.06] text-white font-semibold text-base capitalize transition-colors">
              {r}
            </button>
          ))}
        </div>
      </Drawer>

      <Drawer open={!!lastCreated} onClose={() => setLastCreated(null)} title="Invite created">
        <div className="space-y-4 text-center">
          <p className="text-white/60 text-sm">Share this code with your teammate. It expires in 7 days.</p>
          <div className="glass-card p-6 rounded-2xl">
            <p className="text-3xl font-extrabold text-teal-400 tracking-[0.2em]" data-testid="last-invite-code">{lastCreated?.code}</p>
          </div>
          <div className="flex gap-3 justify-center">
            <button data-testid="copy-new-invite" onClick={() => copyCode(lastCreated?.code)}
              className="flex items-center gap-2 px-5 h-11 rounded-xl border border-teal-500/30 text-teal-400 font-semibold text-sm hover:bg-teal-500/10 transition-colors">
              <Copy size={14} /> Copy
            </button>
          </div>
          <button onClick={() => setLastCreated(null)} className="text-white/50 text-sm font-medium hover:text-white transition-colors">Close</button>
        </div>
      </Drawer>

      <ConfirmModal
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={removeMember}
        title={`Remove ${confirmRemove?.name}?`}
        message="They will lose access immediately. Their historical records remain."
        confirmText="Remove"
        variant="danger"
        loading={removing}
      />
    </div>
  );
}


/* ── Brand Info Tab (Phase 21 — White-Labeling) ── */
const BRAND_BASE = process.env.REACT_APP_BACKEND_URL || '';

function brandRelTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function BrandInfoTab() {
  const { token, hasPlan, tenant } = useAuth();
  const { brand, refreshBrand } = useTenant();
  const toast = useToast();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const canWhiteLabel = hasPlan('pro');

  if (!canWhiteLabel) {
    return (
      <div className="max-w-md" data-testid="brand-info-locked">
        <GlassCard dark className="p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-white/[0.06] border border-white/10 flex items-center justify-center mx-auto mb-4"
            style={{ boxShadow: '0 8px 24px rgba(124,77,255,0.15)' }}>
            <Lock size={28} className="text-white/30" />
          </div>
          <h3 className="text-white font-extrabold text-lg mb-2" data-testid="brand-locked-title">Premium Feature</h3>
          <p className="text-white/50 text-sm mb-6 leading-relaxed">
            White-label your workspace with your own logo. It replaces the 1000AD branding across the dashboard, sidebar, and anywhere your team sees the app.
          </p>
          <Link
            to="/billing"
            data-testid="brand-upgrade-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#7C4DFF] to-[#5B2ED8] text-white font-bold text-sm hover:brightness-110 transition-all shadow-lg shadow-[#7C4DFF]/20"
          >
            <Upload size={16} /> Upgrade to Pro
          </Link>
        </GlassCard>
      </div>
    );
  }

  const hasLogo = brand?.brand_logo_url && tenant?.id;
  const previewSrc = hasLogo
    ? `${BRAND_BASE}/api/brand/${tenant.id}/logo?v=${encodeURIComponent(brand.brand_logo_updated_at || '')}`
    : null;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.show('Logo must be under 2MB for optimal performance.', 'error');
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/svg+xml'].includes(file.type)) {
      toast.show('Only PNG, JPEG, and SVG files are supported.', 'error');
      return;
    }
    setUploading(true);
    try {
      await api.brandLogoUpload(token, file);
      await refreshBrand();
      toast.show('Logo uploaded successfully', 'success');
    } catch (err) {
      toast.show(err?.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await api.brandLogoDelete(token);
      await refreshBrand();
      toast.show('Logo removed — reverted to 1000AD default', 'success');
    } catch (err) {
      toast.show(err?.message || 'Remove failed', 'error');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="max-w-md space-y-6" data-testid="brand-info-tab">
      <div className="glass-card p-6 space-y-5">
        <div>
          <h3 className="text-base font-bold text-white">Your Brand Logo</h3>
          <p className="text-white/50 text-sm mt-1">Replace the 1000AD branding with your own logo across the dashboard, sidebar, and tenant views.</p>
        </div>

        {/* Preview */}
        <div
          className="flex items-center justify-center h-32 rounded-2xl bg-white/[0.04] border border-dashed border-white/15"
          data-testid="brand-logo-preview"
        >
          {hasLogo ? (
            <img
              src={previewSrc}
              alt="Brand logo"
              className="max-h-24 max-w-[200px] object-contain"
              data-testid="brand-logo-current"
            />
          ) : (
            <div className="text-center">
              <ImageIcon size={32} className="text-white/20 mx-auto mb-2" />
              <p className="text-white/30 text-xs">No custom logo — using 1000AD default</p>
            </div>
          )}
        </div>

        {brand?.brand_logo_updated_at && (
          <p className="text-white/35 text-xs" data-testid="brand-logo-updated-at">
            Last updated: {brandRelTime(brand.brand_logo_updated_at)}
          </p>
        )}

        {/* Upload + Remove */}
        <div className="flex gap-3">
          <label className="flex-1 cursor-pointer" data-testid="brand-logo-upload-label">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              onChange={handleUpload}
              className="hidden"
              data-testid="brand-logo-upload-input"
            />
            <span
              className={`flex items-center justify-center gap-2 w-full h-11 rounded-xl bg-gradient-to-r from-[#7C4DFF] to-[#5B2ED8] text-white font-bold text-sm hover:brightness-110 transition-all ${
                uploading ? 'opacity-50 pointer-events-none' : ''
              }`}
            >
              {uploading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Upload size={16} />
              )}
              {uploading ? 'Uploading…' : 'Upload new logo'}
            </span>
          </label>
          {hasLogo && (
            <button
              data-testid="brand-logo-remove-btn"
              onClick={handleRemove}
              disabled={removing}
              className="px-4 h-11 rounded-xl border border-red-500/30 text-red-400 font-semibold text-sm hover:bg-red-500/10 transition-colors disabled:opacity-50"
            >
              {removing ? '…' : 'Remove'}
            </button>
          )}
        </div>

        <div className="space-y-1.5">
          <p className="text-white/30 text-[11px]">PNG, JPEG, or SVG. Max 2 MB. Square logos work best.</p>
          <p className="text-white/30 text-[11px]">Users who add this app to their home screen will see your logo as the app icon.</p>
        </div>
      </div>
    </div>
  );
}
